import React from "react";
import { Card, Row, Col, Badge, ProgressBar, OverlayTrigger, Tooltip } from "react-bootstrap";
import { motion } from "framer-motion";
import { AlertTriangle, Package, TrendingUp, TrendingDown, Minus, CircleDot   } from "lucide-react";

export default function StockDashboard() {
  const stockData = [
    {
      pid: 1,
      name: "HYPROMELLOSE EYE DROPS 0.3% - (GSL)",
      activeQty: 0,
      orderQty: 32214,
      orderReturn: 32,
      stockLost: 220,
      statuses: { ASSESSMENT_REQUIRED: 0, PENDING: 0, SEND_TO_RM: 0, ARCHIVE: 0 },
    },
    {
      pid: 2,
      name: "PARACETAMOL 500MG CAPLETS",
      activeQty: 4200,
      orderQty: 55000,
      orderReturn: 150,
      stockLost: 80,
      statuses: { ASSESSMENT_REQUIRED: 2, PENDING: 1, SEND_TO_RM: 0, ARCHIVE: 0 },
    },
    {
      pid: 3,
      name: "IBUPROFEN 200MG TABLETS",
      activeQty: 1000,
      orderQty: 30000,
      orderReturn: 25,
      stockLost: 300,
      statuses: { ASSESSMENT_REQUIRED: 0, PENDING: 0, SEND_TO_RM: 1, ARCHIVE: 0 },
    },
    {
      pid: 4,
      name: "AMOXICILLIN 250MG CAPSULES",
      activeQty: 15000,
      orderQty: 25000,
      orderReturn: 100,
      stockLost: 50,
      statuses: { ASSESSMENT_REQUIRED: 1, PENDING: 0, SEND_TO_RM: 0, ARCHIVE: 0 },
    },
  ];

  return (
    <div
      className="p-4"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0f3f5, #e0e5eb)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark mb-0">📦 Stock Management Dashboard</h2>
        <Badge bg="dark" className="fs-6 px-3 py-2 d-flex align-items-center gap-1">
        <CircleDot  size={10} className="text-danger" />
        Live Preview
        </Badge>
      </div>

      <Row xs={1} md={2} xl={3} className="g-4">
        {stockData.map((item) => (
          <Col key={item.pid}>
            <StockCard item={item} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

function StockCard({ item }) {
  const total = item.activeQty + item.orderQty + item.orderReturn + item.stockLost;
  const lostPercent = total ? (item.stockLost / total) * 100 : 0;

  // --- Trend ---
  const consumptionRatio = item.activeQty / (item.orderQty + item.orderReturn + item.stockLost);
  let trendLabel, trendColor, trendIcon;

  if (consumptionRatio < 0.1) {
    trendLabel = "Hot";
    trendColor = "danger";
    trendIcon = <TrendingDown size={16} color="red" />;
  } else if (consumptionRatio < 0.5) {
    trendLabel = "Stable";
    trendColor = "warning";
    trendIcon = <Minus size={16} color="gray" />;
  } else {
    trendLabel = "Paused";
    trendColor = "secondary";
    trendIcon = <TrendingUp size={16} color="green" />;
  }

  // --- Reorder & Low stock ---
  const reorderLevel = Math.round(item.orderQty * 0.1);
  const needsReorder = item.activeQty <= reorderLevel;

  // --- Card background for low stock ---
  const cardStyle = needsReorder
    ? { backgroundColor: "#fff5f5", border: "1px solid #f44336" }
    : { backgroundColor: "white" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="shadow-sm rounded-4 h-100" style={cardStyle}>
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <Card.Title className="fw-bold mb-1">{item.name}</Card.Title>
              <small className="text-muted">PID: {item.pid}</small>
            </div>
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip>Trend based on stock vs orders</Tooltip>}
            >
              <div>{trendIcon}</div>
            </OverlayTrigger>
          </div>

          {/* Quantities */}
          <div className="mt-2 mb-2 small text-secondary">
            <Row>
              <Col xs={6}>🟢 Active: <strong>{item.activeQty}</strong></Col>
              <Col xs={6}>📦 Ordered: <strong>{item.orderQty}</strong></Col>
              <Col xs={6}>🔁 Returns: <strong>{item.orderReturn}</strong></Col>
              <Col xs={6}>⚠️ Lost: <strong>{item.stockLost}</strong></Col>
            </Row>
          </div>

          {/* Lost Stock Progress */}
          <ProgressBar
            now={lostPercent}
            variant={lostPercent > 10 ? "danger" : "info"}
            style={{ height: "8px", borderRadius: "6px", marginBottom: "8px" }}
          />

          {/* Statuses */}
          <div className="mt-2 d-flex flex-wrap gap-2">
            {Object.entries(item.statuses).map(([key, val]) => (
              <Badge
                key={key}
                bg="light"
                text="dark"
                className="border fw-normal text-uppercase small"
              >
                {key.replaceAll("_", " ")}: {val}
              </Badge>
            ))}
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  );
}
